import React, { useEffect, useMemo, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Star, ShieldCheck, TrendingUp } from "lucide-react";
import api, { ASSET_ICONS, formatCrypto, formatEUR } from "../lib/api";
import AnimatedNumber from "./AnimatedNumber";

const flagOf = (code) => {
  if (!code || code.length !== 2) return "🌍";
  const A = 127397;
  return String.fromCodePoint(...code.toUpperCase().split("").map((c) => c.charCodeAt(0) + A));
};

const ago = (iso) => {
  const t = new Date(iso).getTime();
  const s = Math.floor((Date.now() - t) / 1000);
  if (s < 60) return `${Math.max(s, 1)}s ago`;
  const m = Math.floor(s / 60);
  if (m < 60) return `${m}m ago`;
  const h = Math.floor(m / 60);
  return `${h}h ago`;
};

export default function TrustStrip() {
  const [items, setItems] = useState([]);
  const [stats, setStats] = useState(null);
  const [idx, setIdx] = useState(0);

  useEffect(() => {
    const load = () => {
      api.get("/activity").then((r) => setItems(r.data.items || [])).catch(() => {});
      api.get("/stats").then((r) => setStats(r.data)).catch(() => {});
    };
    load();
    const id = setInterval(load, 30000);
    return () => clearInterval(id);
  }, []);

  useEffect(() => {
    if (!items.length) return;
    const id = setInterval(() => setIdx((i) => (i + 1) % items.length), 3500);
    return () => clearInterval(id);
  }, [items.length]);

  const current = items[idx];

  const numFormat = useMemo(() => ({
    orders: (v) => Math.round(v).toLocaleString("en-IE"),
    customers: (v) => Math.round(v).toLocaleString("en-IE"),
    volume: (v) => "€" + Math.round(v / 1_000_000).toLocaleString("en-IE") + "M+",
  }), []);

  return (
    <div className="space-y-2 mb-5" data-testid="trust-strip">
      {/* Activity ticker */}
      <div className="bg-white border border-slate-100 rounded-2xl px-3 py-2 flex items-center gap-3 overflow-hidden" data-testid="activity-ticker">
        <div className="flex items-center gap-1.5 shrink-0">
          <span className="relative flex h-2 w-2">
            <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-accent opacity-60" />
            <span className="relative inline-flex rounded-full h-2 w-2 bg-accent" />
          </span>
          <span className="text-[10px] uppercase tracking-wider font-semibold text-slate-500">Live</span>
        </div>
        <AnimatePresence mode="wait">
          {current && (
            <motion.div
              key={`${current.name}-${idx}`}
              initial={{ opacity: 0, y: 8 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -8 }}
              transition={{ duration: 0.25 }}
              className="flex items-center gap-2 text-sm min-w-0 flex-1"
              data-testid="activity-ticker-item"
            >
              <span className="text-base shrink-0">{flagOf(current.country)}</span>
              <span className="font-semibold text-primary truncate">{current.name}</span>
              <span className="text-slate-500 hidden xs:inline">·</span>
              <span className="text-slate-600 truncate">
                bought <span className="font-display font-bold text-primary">{formatCrypto(current.crypto_amount, 4)} {current.asset}</span>
              </span>
              <span className="text-slate-400 text-xs hidden sm:inline">· {formatEUR(current.fiat_amount)}</span>
              <span className="text-slate-400 text-xs ml-auto shrink-0 font-mono">{ago(current.created_at)}</span>
            </motion.div>
          )}
        </AnimatePresence>
      </div>

      {/* Counter pills + Trustpilot */}
      <div className="grid grid-cols-3 gap-2" data-testid="trust-stats">
        <StatPill
          icon={<TrendingUp className="w-3.5 h-3.5 text-accent" />}
          label="Volume" testid="stat-volume"
          value={stats ? <AnimatedNumber value={stats.volume_eur_total} format={numFormat.volume} /> : "…"}
        />
        <StatPill
          icon={<ShieldCheck className="w-3.5 h-3.5 text-accent" />}
          label="Customers" testid="stat-customers"
          value={stats ? <AnimatedNumber value={stats.customers_total} format={numFormat.customers} /> : "…"}
        />
        <a
          href="https://www.trustpilot.com" target="_blank" rel="noreferrer"
          className="bg-white border border-slate-100 rounded-2xl p-2.5 flex items-center gap-1.5 hover:border-slate-200 transition-colors"
          data-testid="trustpilot-pill"
        >
          <div className="flex items-center gap-0.5">
            {[0, 1, 2, 3, 4].map((i) => (
              <Star key={i} className={`w-3 h-3 ${i < Math.floor(stats?.trustpilot_rating || 4.7) ? "fill-[#00B67A] text-[#00B67A]" : "text-slate-300"}`} />
            ))}
          </div>
          <div className="text-[10px] leading-tight flex-1 min-w-0">
            <div className="font-bold text-primary">{stats?.trustpilot_rating ?? "4.7"}</div>
            <div className="text-slate-500 truncate">{stats ? `${stats.trustpilot_reviews.toLocaleString("en-IE")} reviews` : "—"}</div>
          </div>
        </a>
      </div>
    </div>
  );
}

const StatPill = ({ icon, label, value, testid }) => (
  <div className="bg-white border border-slate-100 rounded-2xl p-2.5 min-w-0" data-testid={testid}>
    <div className="flex items-center gap-1 text-[10px] uppercase tracking-wider font-semibold text-slate-500">
      {icon} {label}
    </div>
    <div className="font-display font-extrabold text-primary text-base mt-0.5 truncate">{value}</div>
  </div>
);
